import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Loader2, LogOut, PenSquare, User, Save, X,
  Eye, EyeOff, Globe, KeyRound, MessageCircle, Check, Upload, Reply, CheckCircle2,
} from 'lucide-react';
import {
  getAuthorMe, updateAuthorMe, authorLogout, authorChangePassword,
  listAuthorPosts, createAuthorPost, updateAuthorPost, deleteAuthorPost,
  uploadAuthorPhoto, listAuthorComments, approveComment, replyToComment, deleteComment,
} from '../services/authorApi';
import { toast } from '../hooks/use-toast';
import RichTextEditor from '../components/RichTextEditor';

const emptyPost = () => ({ title: '', slug: '', excerpt: '', cover_image: '', content_html: '', is_public: true, active: true });

const AuthorDashboardPage = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState('posts'); // 'posts' | 'profile' | 'password'
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {mode:'new'|'edit', post}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([getAuthorMe(), listAuthorPosts()]);
      setMe(m);
      setPosts(p.data || []);
      if (m.must_change_password) setTab('password');
    } catch (err) {
      toast({ title: 'Session expired', description: 'Please sign in again.', variant: 'destructive' });
      authorLogout(); nav('/author/login');
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const signOut = () => { authorLogout(); nav('/author/login'); };

  if (loading) return <div className="min-h-screen bg-[#e2ede6] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d]" /></div>;
  if (!me) return null;

  return (
    <div className="min-h-screen bg-[#f7fbf8]">
      {/* Header */}
      <header className="bg-white border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PenSquare className="w-5 h-5 text-[#0f5c3d]" />
            <div>
              <div className="font-serif text-xl text-[#0b1a2e]">Author dashboard</div>
              <div className="text-xs text-[#0b1a2e]/60">{me.name} · <a href={`/people/${me.slug}`} target="_blank" rel="noreferrer" className="text-[#0f5c3d] hover:underline">View public profile</a></div>
            </div>
          </div>
          <button data-testid="author-signout" onClick={signOut} className="inline-flex items-center gap-1.5 text-xs text-[#0b1a2e]/70 hover:text-[#0f5c3d]">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="bg-white border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 flex gap-1">
          {[
            { key: 'posts', label: 'My blog posts', icon: PenSquare },
            { key: 'comments', label: 'Comments', icon: MessageCircle },
            { key: 'profile', label: 'My profile', icon: User },
            { key: 'password', label: 'Change password', icon: KeyRound },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`author-tab-${key}`} onClick={() => setTab(key)}
              className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition ${tab === key ? 'border-[#0f5c3d] text-[#0f5c3d]' : 'border-transparent text-[#0b1a2e]/70 hover:text-[#0f5c3d]'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </nav>

      {me.must_change_password && tab !== 'password' && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
            ⚠️ You are using a temporary password. Please change it in the <button onClick={() => setTab('password')} className="underline font-semibold">Change password</button> tab.
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-8">
        {tab === 'posts' && !editor && (
          <PostsTab posts={posts} onNew={() => setEditor({ mode: 'new', post: emptyPost() })} onEdit={(p) => setEditor({ mode: 'edit', post: p })} onReload={load} />
        )}
        {tab === 'posts' && editor && (
          <PostEditor editor={editor} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} busy={busy} setBusy={setBusy} />
        )}
        {tab === 'comments' && <CommentsTab />}
        {tab === 'profile' && <ProfileTab me={me} onSaved={load} />}
        {tab === 'password' && <PasswordTab onDone={load} mustChange={me.must_change_password} />}
      </main>
    </div>
  );
};

// ─── Tabs ─────────────────────────────────────────────────────────

const PostsTab = ({ posts, onNew, onEdit, onReload }) => {
  const [busy, setBusy] = useState(null);
  const onDelete = async (p) => {
    if (!window.confirm(`Delete post "${p.title}"?`)) return;
    setBusy(p.id);
    try { await deleteAuthorPost(p.id); toast({ title: 'Deleted' }); onReload(); } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); } finally { setBusy(null); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-[#0b1a2e]">My blog posts <span className="text-sm text-[#0b1a2e]/50 ml-2">({posts.length})</span></h1>
        <button data-testid="author-new-post" onClick={onNew} className="inline-flex items-center gap-1.5 bg-[#0f5c3d] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[#0b4d33]">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>
      {posts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-12 text-center text-[#0b1a2e]/60">
          <PenSquare className="w-8 h-8 text-[#0f5c3d]/40 mx-auto mb-3" />
          You haven't written any posts yet. Click <b>New post</b> to publish your first.
        </div>
      ) : (
        <div className="grid gap-3">
          {posts.map((p) => (
            <div key={p.id} data-testid="author-post-card" className="bg-white rounded-xl border border-black/5 p-4 flex items-center gap-4 hover:shadow-sm transition">
              {p.cover_image ? <img src={p.cover_image} alt="" className="w-20 h-20 rounded-lg object-cover" /> : <div className="w-20 h-20 rounded-lg bg-[#e2ede6] flex items-center justify-center"><PenSquare className="w-6 h-6 text-[#0f5c3d]/40" /></div>}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#0b1a2e]">{p.title}</div>
                <div className="text-xs text-[#0b1a2e]/60 mt-1 truncate">/{p.slug}</div>
                <div className="text-xs text-[#0b1a2e]/50 mt-1 flex items-center gap-2">
                  {p.is_public ? <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="w-3 h-3" /> Public</span>
                    : <span className="inline-flex items-center gap-1 text-amber-700"><EyeOff className="w-3 h-3" /> Draft</span>}
                  {p.published_at && <span>· {new Date(p.published_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <button onClick={() => onEdit(p)} data-testid="author-edit-post" className="p-2 hover:bg-black/5 rounded-lg"><Pencil className="w-4 h-4 text-[#0f5c3d]" /></button>
              <button onClick={() => onDelete(p)} disabled={busy === p.id} data-testid="author-delete-post" className="p-2 hover:bg-red-50 rounded-lg disabled:opacity-50">{busy === p.id ? <Loader2 className="w-4 h-4 animate-spin text-red-500" /> : <Trash2 className="w-4 h-4 text-red-500" />}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PostEditor = ({ editor, onClose, onSaved, busy, setBusy }) => {
  const [form, setForm] = useState(editor.post);
  const save = async () => {
    if (form.title.length < 3) { toast({ title: 'Title too short', variant: 'destructive' }); return; }
    if ((form.content_html || '').length < 10) { toast({ title: 'Please write some content', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      if (editor.mode === 'new') await createAuthorPost(form);
      else await updateAuthorPost(form.id, form);
      toast({ title: 'Saved' }); onSaved();
    } catch (err) { toast({ title: 'Save failed', description: err.response?.data?.detail || String(err), variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-xl">{editor.mode === 'new' ? 'New blog post' : 'Edit post'}</h1>
        <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Title</label>
          <input data-testid="author-post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Excerpt (short teaser)</label>
          <input data-testid="author-post-excerpt" value={form.excerpt || ''} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" placeholder="Optional — shown on card" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Cover image URL</label>
          <input data-testid="author-post-cover" value={form.cover_image || ''} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" placeholder="Paste a hosted URL (or use admin upload later)" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Content</label>
          <RichTextEditor value={form.content_html || ''} onChange={(html) => setForm({ ...form, content_html: html })} />
        </div>
        <label className="inline-flex items-center gap-2">
          <input data-testid="author-post-public" type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4" />
          <span className="text-sm">Publish immediately (uncheck to save as draft)</span>
        </label>
        <div className="pt-4 border-t border-black/5 flex gap-2">
          <button onClick={save} disabled={busy} data-testid="author-post-save" className="inline-flex items-center gap-1.5 bg-[#0f5c3d] text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 border border-[#0f5c3d]/20 rounded-full px-5 py-2 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const ProfileTab = ({ me, onSaved }) => {
  const [form, setForm] = useState({
    short_desc: me.short_desc || '',
    long_desc: me.long_desc || '',
    photo_url: me.photo_url || '',
    socials: {
      linkedin: '', twitter: '', facebook: '', instagram: '', youtube: '', email: '', website: '',
      ...(me.socials || {}),
    },
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await updateAuthorMe(form); toast({ title: 'Profile updated' }); onSaved(); }
    catch (err) { toast({ title: 'Save failed', description: err.response?.data?.detail || String(err), variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const setSoc = (k, v) => setForm({ ...form, socials: { ...form.socials, [k]: v } });
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-3xl">
      <h1 className="font-serif text-2xl text-[#0b1a2e] mb-6">My profile</h1>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Photo</label>
          <div className="flex items-center gap-4">
            {form.photo_url ? (
              <img src={form.photo_url} alt="" className="w-24 h-24 rounded-full object-cover border border-black/5" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#e2ede6] flex items-center justify-center"><User className="w-8 h-8 text-[#0f5c3d]/40" /></div>
            )}
            <div className="flex flex-col gap-2">
              <label data-testid="author-photo-upload" className="inline-flex items-center gap-1.5 border border-[#0f5c3d]/25 text-[#0f5c3d] rounded-full px-4 py-2 text-xs font-medium cursor-pointer hover:bg-[#0f5c3d]/5">
                <Upload className="w-3.5 h-3.5" /> Upload new photo
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  try { const r = await uploadAuthorPhoto(f); setForm({ ...form, photo_url: r.url }); toast({ title: 'Uploaded' }); }
                  catch (err) { toast({ title: 'Upload failed', description: err.response?.data?.detail || String(err), variant: 'destructive' }); }
                }} />
              </label>
              <input data-testid="author-photo" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="…or paste a URL" className="border border-[#0f5c3d]/15 rounded-lg px-3 py-1.5 text-xs w-64" />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Short bio (one line)</label>
          <input data-testid="author-shortdesc" value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Long bio</label>
          <RichTextEditor value={form.long_desc} onChange={(html) => setForm({ ...form, long_desc: html })} />
        </div>
        <div className="pt-3 border-t border-black/5">
          <div className="text-sm font-semibold text-[#0b1a2e] mb-1">Social handles</div>
          <p className="text-xs text-[#0b1a2e]/60 mb-3">Paste a full URL or just the handle (e.g. <code>@handle</code>). We auto-link to the platform.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['linkedin', 'twitter', 'facebook', 'instagram', 'youtube', 'email', 'website'].map((k) => (
              <label key={k} className="block">
                <span className="block text-xs font-medium text-[#0b1a2e]/70 mb-1 capitalize">{k === 'twitter' ? 'X (Twitter)' : k}</span>
                <input data-testid={`author-social-${k}`} value={form.socials[k] || ''} onChange={(e) => setSoc(k, e.target.value)} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
              </label>
            ))}
          </div>
        </div>
        <div className="pt-4 border-t border-black/5">
          <button onClick={save} disabled={busy} data-testid="author-profile-save" className="inline-flex items-center gap-1.5 bg-[#0f5c3d] text-white rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile</button>
        </div>
      </div>
    </div>
  );
};

const PasswordTab = ({ onDone, mustChange }) => {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [cf, setCf] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast({ title: 'Password must be ≥ 8 characters', variant: 'destructive' }); return; }
    if (nw !== cf) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    setBusy(true);
    try { await authorChangePassword(cur, nw); toast({ title: 'Password changed' }); setCur(''); setNw(''); setCf(''); onDone(); }
    catch (err) { toast({ title: 'Change failed', description: err.response?.data?.detail || String(err), variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-6 max-w-md space-y-4">
      <h1 className="font-serif text-xl text-[#0b1a2e]">{mustChange ? 'Set a new password' : 'Change password'}</h1>
      {mustChange && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-xs">You are still using the temporary password from your invite email. Please set a new one now.</div>}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Current password</label>
        <input data-testid="author-current-pw" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">New password (≥ 8 chars)</label>
        <input data-testid="author-new-pw" type="password" required minLength={8} value={nw} onChange={(e) => setNw(e.target.value)} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Confirm new password</label>
        <input data-testid="author-confirm-pw" type="password" required value={cf} onChange={(e) => setCf(e.target.value)} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
      </div>
      <button data-testid="author-change-pw-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0f5c3d] text-white rounded-full py-2.5 text-sm font-semibold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Change password</button>
    </form>
  );
};

export default AuthorDashboardPage;

// ─── Comments moderation tab ──────────────────────────────────────

const CommentsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending

  const load = () => {
    setLoading(true);
    listAuthorComments(filter === 'pending').then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const approve = async (id, approved) => { await approveComment(id, approved); toast({ title: approved ? 'Approved' : 'Hidden' }); load(); };
  const del = async (id) => { if (!window.confirm('Delete comment?')) return; await deleteComment(id); toast({ title: 'Deleted' }); load(); };
  const submitReply = async (id) => {
    if (replyText.trim().length < 2) return;
    try { await replyToComment(id, replyText.trim()); toast({ title: 'Reply posted' }); setReplyFor(null); setReplyText(''); load(); }
    catch (err) { toast({ title: 'Failed', description: err.response?.data?.detail || String(err), variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-[#0b1a2e]">Comments on my posts</h1>
        <div className="inline-flex bg-white border border-black/5 rounded-full p-0.5">
          {[['all', 'All'], ['pending', 'Pending']].map(([k, l]) => (
            <button key={k} data-testid={`comments-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-xs font-medium ${filter === k ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/60'}`}>{l}</button>
          ))}
        </div>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#0f5c3d]" />
        : items.length === 0 ? <p className="text-sm text-[#0b1a2e]/60 bg-white rounded-xl border border-black/5 p-8 text-center">No comments {filter === 'pending' ? 'pending review' : 'yet'}.</p>
        : (
          <div className="space-y-3">
            {items.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-black/5 p-4" data-testid="author-comment-row">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-[#0b1a2e]">{c.author_name}</span>
                      {c.is_author_reply && <span className="text-[10px] bg-[#0f5c3d] text-white px-1.5 py-0.5 rounded font-semibold">Your reply</span>}
                      {!c.approved && !c.is_author_reply && <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-semibold">Pending</span>}
                      {c.approved && !c.is_author_reply && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-semibold">Live</span>}
                      <span className="text-xs text-[#0b1a2e]/50">on <b>{c.post_title}</b></span>
                    </div>
                    <p className="mt-2 text-sm text-[#0b1a2e] whitespace-pre-wrap">{c.body}</p>
                    {c.author_email && !c.is_author_reply && <p className="mt-1 text-xs text-[#0b1a2e]/40">{c.author_email}</p>}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {!c.is_author_reply && (
                      <>
                        {!c.approved && <button onClick={() => approve(c.id, true)} data-testid="approve-comment" title="Approve" className="p-1.5 hover:bg-emerald-50 rounded-lg text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>}
                        {c.approved && <button onClick={() => approve(c.id, false)} title="Hide" className="p-1.5 hover:bg-amber-50 rounded-lg text-amber-600"><EyeOff className="w-4 h-4" /></button>}
                        <button onClick={() => { setReplyFor(c.id); setReplyText(''); }} data-testid="reply-comment" title="Reply" className="p-1.5 hover:bg-[#0f5c3d]/10 rounded-lg text-[#0f5c3d]"><Reply className="w-4 h-4" /></button>
                      </>
                    )}
                    <button onClick={() => del(c.id)} title="Delete" className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {replyFor === c.id && (
                  <div className="mt-3 pt-3 border-t border-black/5 flex gap-2">
                    <input autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type your reply…" data-testid="reply-input" className="flex-1 border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
                    <button onClick={() => submitReply(c.id)} data-testid="submit-reply" className="bg-[#0f5c3d] text-white rounded-lg px-3 py-2 text-xs font-semibold">Send</button>
                    <button onClick={() => setReplyFor(null)} className="px-3 py-2 text-xs">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};
